import { memo, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { ReplayBar } from '../replay/ReplayBar';
import type { IReplayController } from '../replay/useReplayController';
import { BuildingCardControls } from './BuildingCardControls';
import { SpecialAreas } from './SpecialAreas';
import { useBuildingCard } from './useBuildingCard';
import { useDockClearance } from './useDockClearance';
import barStyles from '../replay/ReplayBar.module.css';
import styles from './mainView.module.css';

interface IMainViewProps {
  /** The Monitor's replay; absent where the card renders without it. */
  replay?: IReplayController;
}

function MainViewInner({ replay }: IMainViewProps) {
  const { t } = useTranslation();
  const { cardClassName, buildingClassName, cardLabel, building, failed } = useBuildingCard(replay);
  const stamp = replay?.stamp;
  const { dockRef, clearance } = useDockClearance();

  return (
    <div className={styles.mainView}>
      <ScrollArea
        className={styles.body}
        viewportClassName={styles.bodyViewport}
        viewportProps={{ style: { '--replay-bar-clearance': `${clearance}px` } as CSSProperties }}
      >
        <section className={cardClassName} aria-label={cardLabel}>
          <header className={styles.cardHeaderRow}>
            <h2 className={styles.cardHeaderTitle}>
              {stamp ? t('monitor.replay.cardTitle', { ...stamp }) : t('monitor.buildingCard.title')}
            </h2>
            <BuildingCardControls replay={replay} />
          </header>
          {/* .buildingWrap stays unchanged — BuildingViewSvg's `getBBox()`
              measurement assumes this exact wrapping geometry. */}
          <div className={styles.buildingWrap}>
            <div className={buildingClassName}>{building}</div>
          </div>
          {failed}
          {/* Portalled: it floats over the whole screen, sidebar and rail included, as in fullscreen. */}
          {replay?.barProps &&
            createPortal(
              <div ref={dockRef} className={barStyles.dock}>
                <ReplayBar {...replay.barProps} />
              </div>,
              document.body,
            )}
        </section>

        <SpecialAreas />
      </ScrollArea>
    </div>
  );
}

/** Memoized: the page root re-renders on every replay tick; this card only when its replay changes. */
export const MainView = memo(MainViewInner);
